"use client";

import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useGetAttributes } from "@faire/web--source/slate/Button/utils";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import { useMaybeDomainStripping } from "@faire/web--source/ui/hooks/useMaybeDomainStripping";
import React from "react";
import styled from "styled-components";

import { useShouldShowSpinner } from "../../ui/hooks/useShouldShowSpinner";
import {
  highVisSlateComponent,
  HIGH_VIS_FILTER,
} from "../utils/highVisSlateComponent";

import { IconButtonStyles, IconWrapperStyles } from "./styles";
import { iconWrapperTheme } from "./theme";
import {
  IconButtonProps,
  StyledIconButtonProps,
  StyledIconWrapperProps,
} from "./types";

/**
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/slate/component/icon-button)
 */
export const IconButton: React.FC<IconButtonProps> = ({
  children,
  ...props
}) => {
  const { strictLocalize } = useStrictLocalization();
  const {
    "aria-label": ariaLabel,
    buttonRef,
    "data-test-id": dataTestId,
    disabled = false,
    loading = false,
    onClick,
    shape = "circle",
    size = "M",
    variant = "primary",
    linkTo,
    ...rest
  } = props;

  const { isDisabled, handleClick, shouldShowSpinner } = useShouldShowSpinner(
    disabled,
    loading,
    onClick
  );

  const {
    potentiallyRelativeLink = linkTo,
    handleClickWithDomainStrippingAssignment = handleClick,
  } = useMaybeDomainStripping(linkTo, handleClick, undefined);

  const { as, linkProps } = useGetAttributes({
    ...props,
    linkTo: potentiallyRelativeLink,
  });

  const loadingMessage = strictLocalize({
    defaultMessage: "Loading",
    description: {
      text: "Message for screen readers when buttons just show a loading spinner with no text",
    },
  });

  return (
    <StyledIconButton
      aria-disabled={isDisabled}
      aria-label={shouldShowSpinner ? loadingMessage : ariaLabel}
      aria-live="polite"
      disabled={isDisabled}
      data-test-id={dataTestId}
      $loading={loading}
      as={as}
      onClick={handleClickWithDomainStrippingAssignment}
      ref={buttonRef}
      $shape={shape}
      $variant={variant}
      {...rest}
      {...linkProps}
    >
      <StyledIconWrapper
        aria-hidden={true}
        disabled={isDisabled}
        $shape={shape}
        $size={size}
        $variant={variant}
      >
        {shouldShowSpinner ? (
          <StyledLoadingSpinner variant={variant} />
        ) : (
          children
        )}
      </StyledIconWrapper>
    </StyledIconButton>
  );
};
IconButton.displayName = "IconButton";

export const StyledIconButton = styled.button<StyledIconButtonProps>`
  ${IconButtonStyles}
  ${highVisSlateComponent(HIGH_VIS_FILTER.COMPONENT)}
`;

export const StyledIconWrapper = styled.div<StyledIconWrapperProps>`
  ${IconWrapperStyles}
`;

type StyledLoadingSpinnerProps = Pick<IconButtonProps, "variant">;
const StyledLoadingSpinner = styled(
  LoadingSpinner
).attrs<StyledLoadingSpinnerProps>((props) => {
  const { variant = "primary" } = props;
  const IconButtonVariantTheme = iconWrapperTheme[variant].loading;
  const { color } =
    typeof IconButtonVariantTheme === "function"
      ? IconButtonVariantTheme({ $variant: variant })
      : IconButtonVariantTheme;

  return { color };
})<StyledLoadingSpinnerProps>``;
